import { __extends } from "tslib";
import { SpecularCompositeMethod } from './SpecularCompositeMethod';
/**
 * SpecularCelMethod provides a shading method to add specular cel (cartoon)
 * shading.
 */
var SpecularCelMethod = /** @class */ (function (_super) {
    __extends(SpecularCelMethod, _super);
    /**
     * Creates a new SpecularCelMethod object.
     * @param specularCutOff The threshold at which the specular highlight
     * should be shown.
     * @param smoothness The smoothness of the highlight edge.
     * @param baseMethod An optional specular method on which the cartoon
     * shading is based. If ommitted, SpecularBasicMethod is used.
     */
    function SpecularCelMethod(specularCutOff, smoothness, baseMethod) {
        if (specularCutOff === void 0) { specularCutOff = 0.5; }
        if (smoothness === void 0) { smoothness = 0.1; }
        if (baseMethod === void 0) { baseMethod = null; }
        var _this = _super.call(this, baseMethod) || this;
        _this._specularCutOff = specularCutOff;
        _this._smoothness = smoothness;
        return _this;
    }
    Object.defineProperty(SpecularCelMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return SpecularCelMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SpecularCelMethod.prototype, "smoothness", {
        /**
         * The smoothness of the highlight edge.
         */
        get: function () {
            return this._smoothness;
        },
        set: function (value) {
            if (this._smoothness == value)
                return;
            this._smoothness = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SpecularCelMethod.prototype, "specularCutOff", {
        /**
         * The threshold at which the specular highlight should be shown.
         */
        get: function () {
            return this._specularCutOff;
        },
        set: function (value) {
            if (this._specularCutOff == value)
                return;
            this._specularCutOff = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    SpecularCelMethod.assetType = '[asset SpecularCelMethod]';
    return SpecularCelMethod;
}(SpecularCompositeMethod));
export { SpecularCelMethod };
import { ShaderBase } from '@awayjs/renderer';
import { _Shader_LightingCompositeMethod } from './CompositeMethodBase';
/**
 * _Shader_SpecularCelMethod provides a shading method to add specular cel
 * (cartoon) shading.
 */
var _Shader_SpecularCelMethod = /** @class */ (function (_super) {
    __extends(_Shader_SpecularCelMethod, _super);
    /**
     * Creates a new DiffuseCelChunk object.
     * @param levels The amount of shadow gradations.
     * @param baseMethod An optional diffuse method on which the cartoon shading
     * is based. If omitted, DiffuseBasicMethod is used.
     */
    function _Shader_SpecularCelMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        _this._baseChunk._modulateFunction = function (targetReg, registerCache, sharedRegisters) { return _this.clampSpecular(targetReg, registerCache, sharedRegisters); };
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_SpecularCelMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        if (this._invalid) {
            var index = this._cutoffDataIndex;
            var data = this._shader.fragmentConstantData;
            data[index] = this._method.smoothness;
            data[index + 1] = this._method.specularCutOff;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularCelMethod.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._cutoffDataRegister = null;
    };
    /**
     * Snaps the specular shading strength of the wrapped method to zero or one, depending on whether or not it exceeds the specularCutOff
     * @param vo The MethodVO used to compile the current shader.
     * @param t The register containing the specular strength in the "w" component, and either the half-vector or the reflection vector in "xyz".
     * @param regCache The register cache used for the shader compilation.
     * @param sharedRegisters The shared register data for this shader.
     * @return The AGAL fragment code for the method.
     */
    _Shader_SpecularCelMethod.prototype.clampSpecular = function (targetReg, registerCache, sharedRegisters) {
        return 'sub ' + targetReg + '.y, ' + targetReg + '.w, ' + this._cutoffDataRegister + '.y\n' + // x - cutoff
            'div ' + targetReg + '.y, ' + targetReg + '.y, ' + this._cutoffDataRegister + '.x\n' + // (x - cutoff)/epsilon
            'sat ' + targetReg + '.y, ' + targetReg + '.y\n' +
            'sge ' + targetReg + '.w, ' + targetReg + '.w, ' + this._cutoffDataRegister + '.y\n' +
            'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + targetReg + '.y\n';
    };
    /**
     * @inheritDoc
     */
    _Shader_SpecularCelMethod.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        this._cutoffDataRegister = registerCache.getFreeFragmentConstant();
        this._cutoffDataIndex = this._cutoffDataRegister.index * 4;
        return _super.prototype._getFragmentPreLightingCode.call(this, registerCache, sharedRegisters);
    };
    return _Shader_SpecularCelMethod;
}(_Shader_LightingCompositeMethod));
export { _Shader_SpecularCelMethod };
ShaderBase.registerAbstraction(_Shader_SpecularCelMethod, SpecularCelMethod);
